import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import { Bar } from "react-chartjs-2";
import { GetDashboardInfoResponseTypes, DashboardClassTypes } from "src/redux/actions/dashboard/types";
import { GRIFFLE_TYPE, GRIFFLE_RADIUS } from "src/modules/griffle/griffleTheme";
import { IconChart } from "src/modules/griffle/GriffleIcons";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/** Read a CSS custom-property value at runtime (needed for JS-based chart configs). */
const cssVar = (prop: string): string =>
  getComputedStyle(document.querySelector(".griffle-page") ?? document.documentElement)
    .getPropertyValue(prop)
    .trim();

// ── Griffle palette for class bars ──────────────────────────────────────────
const CLASS_COLORS = [
  "#539a18", // Griffle primary green
  "#3b82f6", // blue
  "#f59e0b", // amber
  "#8b5cf6", // purple
  "#ef4444", // red
  "#06b6d4", // cyan
  "#ec4899", // pink
  "#10b981" // emerald
];

// ── Styles ───────────────────────────────────────────────────────────────────
const ChartWrap = styled.div`
  font-family: var(--font-body);

  .chart-controls {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 20px;
  }

  .controls-label {
    font-size: ${GRIFFLE_TYPE.small};
    font-weight: ${GRIFFLE_TYPE.weightSemi};
    color: var(--color-ink-muted);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-right: 4px;
    white-space: nowrap;
  }

  .class-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: ${GRIFFLE_RADIUS.pill};
    border: 1.5px solid transparent;
    font-size: ${GRIFFLE_TYPE.small};
    font-weight: ${GRIFFLE_TYPE.weightMd};
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
    background: var(--color-surface);
    color: var(--color-ink-muted);
    border-color: var(--color-border);

    .pill-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    &.active {
      border-color: transparent;
      color: #fff;
    }

    &:hover:not(.active) {
      border-color: var(--color-ink-muted);
      color: var(--color-ink);
    }
  }

  .select-all-btn {
    padding: 5px 12px;
    border-radius: ${GRIFFLE_RADIUS.pill};
    border: 1.5px solid var(--color-border);
    font-size: ${GRIFFLE_TYPE.small};
    font-weight: ${GRIFFLE_TYPE.weightMd};
    cursor: pointer;
    background: transparent;
    color: var(--color-ink-muted);
    transition: all 0.15s;
    font-family: var(--font-body);

    &:hover {
      border-color: var(--color-primary);
      color: var(--color-primary);
    }
  }

  .chart-body {
    height: 340px;
    position: relative;
  }

  .chart-summary {
    display: flex;
    gap: 24px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--color-border);
    flex-wrap: wrap;
  }

  .summary-stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .summary-value {
    font-family: var(--font-heading);
    font-weight: ${GRIFFLE_TYPE.weightBold};
    font-size: 20px;
    color: var(--color-ink);
    line-height: 1;
  }

  .summary-label {
    font-size: ${GRIFFLE_TYPE.xsmall};
    color: var(--color-ink-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: ${GRIFFLE_TYPE.weightMd};
  }
`;

const EmptyState = styled.div`
  height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--color-ink-muted);
  font-size: ${GRIFFLE_TYPE.body};
  border: 2px dashed var(--color-border);
  border-radius: ${GRIFFLE_RADIUS.button};
  background: var(--color-page-bg);

  .icon {
    opacity: 0.35;
    color: var(--color-ink-muted);
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────
interface Props {
  data?: GetDashboardInfoResponseTypes;
}

const GriffleReadingChart: React.FC<Props> = ({ data }) => {
  const classes: DashboardClassTypes[] = data?.total?.classses ?? [];

  // Build a stable label for each class — append groupId when names collide
  const classLabel = useMemo(() => {
    const nameCounts: Record<string, number> = {};
    classes.forEach((c) => {
      nameCounts[c.className] = (nameCounts[c.className] ?? 0) + 1;
    });
    const map: Record<number, string> = {};
    classes.forEach((c) => {
      map[c.groupId] = nameCounts[c.className] > 1 ? `${c.className} (${c.groupId})` : c.className;
    });
    return map;
  }, [classes]);

  // All group IDs in stable order
  const allGroupIds = useMemo(() => classes.map((c) => c.groupId), [classes]);

  // Initially all selected — keyed by groupId
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set(allGroupIds));

  // Keep selection in sync when data loads
  useEffect(() => {
    setSelectedIds(new Set(allGroupIds));
  }, [allGroupIds.join(",")]); // intentional: stringify for primitive comparison

  const toggleClass = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (next.size > 1) next.delete(id); // keep at least one
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(allGroupIds));
  const allSelected = selectedIds.size === allGroupIds.length;

  // Derive sorted reading-level labels from selected classes only
  const chartData = useMemo(() => {
    const filtered = classes.filter((c) => selectedIds.has(c.groupId));
    if (!filtered.length) return null;

    const allLevels = filtered.flatMap((c) => c.readingLevelStats.map((s) => s.readingLevel));
    const sortedLevels = Array.from(new Set(allLevels)).sort((a, b) => a - b);

    const datasets = filtered.map((c) => {
      const colorIdx = allGroupIds.indexOf(c.groupId) % CLASS_COLORS.length;
      const color = CLASS_COLORS[colorIdx];
      return {
        label: classLabel[c.groupId],
        data: sortedLevels.map((level) => {
          const stat = (c.readingLevelStats ?? []).find((s) => s.readingLevel === level);
          return stat?.numberOfStudents ?? 0;
        }),
        backgroundColor: color,
        hoverBackgroundColor: color,
        borderRadius: 4,
        borderSkipped: false as const
      };
    });

    return { labels: sortedLevels, datasets };
  }, [classes, selectedIds, allGroupIds, classLabel]);

  // Summary stats for selected classes
  const totalStudents = useMemo(() => {
    return classes
      .filter((c) => selectedIds.has(c.groupId))
      .flatMap((c) => c.readingLevelStats ?? [])
      .reduce((sum, s) => sum + (s?.numberOfStudents ?? 0), 0);
  }, [classes, selectedIds]);

  const avgReadingLevel = useMemo(() => {
    const selected = classes.filter((c) => selectedIds.has(c.groupId));
    const allStats = selected.flatMap((c) => c.readingLevelStats ?? []);
    const totalStuds = allStats.reduce((s, x) => s + (x?.numberOfStudents ?? 0), 0);
    if (!totalStuds) return null;
    const weightedSum = allStats.reduce(
      (s, x) => s + (x?.readingLevel ?? 0) * (x?.numberOfStudents ?? 0),
      0
    );
    return (weightedSum / totalStuds).toFixed(1);
  }, [classes, selectedIds]);

  const levelRange = useMemo(() => {
    const selected = classes.filter((c) => selectedIds.has(c.groupId));
    const levels = selected.flatMap((c) =>
      (c.readingLevelStats ?? []).filter((s) => (s?.numberOfStudents ?? 0) > 0).map((s) => s.readingLevel)
    );
    if (!levels.length) return null;
    return { min: Math.min(...levels), max: Math.max(...levels) };
  }, [classes, selectedIds]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        padding: 10,
        backgroundColor: "#fff",
        borderColor: cssVar("--color-border"),
        borderWidth: 1,
        titleColor: cssVar("--color-ink"),
        bodyColor: cssVar("--color-ink-muted"),
        titleFont: { size: 12, family: cssVar("--font-body"), weight: "600" as const },
        bodyFont: { size: 12, family: cssVar("--font-body") },
        cornerRadius: 8,
        callbacks: {
          title: (items: any[]) => `Reading Level ${items[0].label}`,
          label: (item: any) => `${item.dataset.label}: ${item.raw} student${item.raw !== 1 ? "s" : ""}`
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          font: { size: 12, family: cssVar("--font-body") },
          color: cssVar("--color-ink-muted")
        },
        title: {
          display: true,
          text: "Reading Level",
          font: { size: 12, family: cssVar("--font-body") },
          color: cssVar("--color-ink-muted")
        }
      },
      y: {
        beginAtZero: true,
        grace: "10%",
        grid: {
          color: cssVar("--color-border"),
          lineWidth: 1
        },
        border: { display: false, dash: [4, 4] },
        ticks: {
          font: { size: 12, family: cssVar("--font-body") },
          color: cssVar("--color-ink-muted"),
          stepSize: 1
        },
        title: {
          display: true,
          text: "Students",
          font: { size: 12, family: cssVar("--font-body") },
          color: cssVar("--color-ink-muted")
        }
      }
    }
  };

  if (!classes.length) {
    return (
      <EmptyState>
        <IconChart size={32} className="icon" />
        <span>No reading data available yet</span>
      </EmptyState>
    );
  }

  return (
    <ChartWrap>
      {/* ── Class filter pills ── */}
      <div className="chart-controls">
        <span className="controls-label">Classes:</span>
        {classes.map((c, i) => {
          const color = CLASS_COLORS[i % CLASS_COLORS.length];
          const isActive = selectedIds.has(c.groupId);
          return (
            <button
              key={c.groupId}
              className={`class-pill${isActive ? " active" : ""}`}
              style={
                isActive
                  ? { backgroundColor: color, borderColor: color }
                  : { borderColor: color + "66", color: "var(--color-ink-muted)" }
              }
              onClick={() => toggleClass(c.groupId)}
              title={`${isActive ? "Hide" : "Show"} ${classLabel[c.groupId]}`}
            >
              <span className="pill-dot" style={{ backgroundColor: isActive ? "#fff" : color }} />
              {classLabel[c.groupId]}
            </button>
          );
        })}
        {!allSelected && (
          <button className="select-all-btn" onClick={selectAll}>
            Show all
          </button>
        )}
      </div>

      {/* ── Bar chart ── */}
      {chartData ? (
        <div className="chart-body">
          <Bar options={chartOptions} data={chartData} />
        </div>
      ) : (
        <EmptyState>
          <IconChart size={32} className="icon" />
          <span>Select at least one class to view the chart</span>
        </EmptyState>
      )}

      {/* ── Summary row ── */}
      {(totalStudents > 0 || avgReadingLevel) && (
        <div className="chart-summary">
          <div className="summary-stat">
            <span className="summary-value">{totalStudents}</span>
            <span className="summary-label">
              {selectedIds.size === allGroupIds.length
                ? "Total students"
                : `Students in ${selectedIds.size} class${selectedIds.size > 1 ? "es" : ""}`}
            </span>
          </div>
          {avgReadingLevel && (
            <div className="summary-stat">
              <span className="summary-value">{avgReadingLevel}</span>
              <span className="summary-label">Avg. reading level</span>
            </div>
          )}
          {levelRange && (
            <div className="summary-stat">
              <span className="summary-value">
                {levelRange.min}–{levelRange.max}
              </span>
              <span className="summary-label">Level range</span>
            </div>
          )}
          <div className="summary-stat">
            <span className="summary-value">{selectedIds.size}</span>
            <span className="summary-label">
              {selectedIds.size === allGroupIds.length
                ? "Classes (all)"
                : `Class${selectedIds.size > 1 ? "es" : ""} selected`}
            </span>
          </div>
        </div>
      )}
    </ChartWrap>
  );
};

export default GriffleReadingChart;
